import React from "react";
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  Link,
  Avatar,
  Grid,
  IconButton,
  Paper,
  Divider,
  CardMedia,
  Button,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { blogData, blogCards } from "./blogPost";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../style/theme";

export default function BlogTemplate() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.replace("/blog/", "");

  const featureData = blogData[path];
  let nextBlogIndex = featureData["id"] + 1;

  if (nextBlogIndex === blogCards.length) {
    nextBlogIndex = 0;
  }
  const nextBlogCard = blogCards[nextBlogIndex];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          overflow: "hidden",
          ml: { lg: "19%" },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            textAlign: "left",
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // stack on mobile, row on desktop
            justifyContent: "space-between",
            alignItems: "flex-start",
            px: { xs: 2, sm: 4 },
          }}
        >
          {/* Left Section */}
          <Grid sx={{ flex: 1, mr: { md: 3 } }}>
            {/* Back Button */}
            <Grid
              sx={{
                mb: 4,
                boxSizing: "border-box",
                width: "100%",
                fontSize: { xs: "0.85em", sm: "0.9em" },
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon fontSize="small" sx={{ color: "black" }} />
              </IconButton>
              Back to Blog
            </Grid>

            {/* Category */}
            <Grid
              sx={{
                backgroundColor: "rgb(239, 240, 240)",
                mb: 3,
                borderRadius: "14px",
                boxSizing: "border-box",
                p: 0.6,
                px: 0.8,
                display: "inline-flex",
                fontSize: { xs: "0.85em", sm: "0.9em" },
                fontFamily: '"Inter", sans-serif',
                alignItems: "center",
              }}
            >
              By Awsmo Team
            </Grid>

            {/* Title */}
            <Typography
              sx={{
                mb: 4,
                lineHeight: { xs: "36px", sm: "42px", md: "49px" },
                fontSize: { xs: "1.8em", sm: "2.3em", md: "2.9em" },
                fontWeight: 800,
                color: "#252525ff",
                fontFamily: '"Roboto", sans-serif',
              }}
            >
              {featureData["head"]}
            </Typography>

            {/* Subhead */}
            <Typography
              sx={{
                color: "rgb(110, 110, 110)",
                fontSize: { xs: "1em", md: "1.2em" },
              }}
            >
              {featureData["subHead"]}
            </Typography>

            <Divider sx={{ my: 2.4 }} />

            {/* Meta Info */}
            <Grid
              container
              columnGap={3}
              direction="row"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 300,
                color: "rgb(125, 130, 138)",
                display: "flex",
                alignItems: "center",
                fontSize: "0.9em",
              }}
            >
              <Grid item sx={{ display: "flex", alignItems: "center" }}>
                <DateRangeOutlinedIcon
                  sx={{ color: "inherit", fontSize: "0.9em", mr: 0.5 }}
                />
                {featureData["date"]}
              </Grid>
              <Grid item sx={{ display: "flex", alignItems: "center" }}>
                <AccessTimeOutlinedIcon
                  sx={{ color: "inherit", fontSize: "0.9em", mr: 0.5 }}
                />
                {featureData["time"]}
              </Grid>
              <Grid item sx={{ display: "flex", alignItems: "center" }}>
                <RemoveRedEyeOutlinedIcon
                  sx={{ color: "inherit", fontSize: "0.9em", mr: 0.5 }}
                />
                {featureData["views"]}
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.6 }} />

            {/* Main Image */}
            <Box
              sx={{
                borderRadius: 2,
                textAlign: "left",
                alignItems: "center",
              }}
            >
              <img
                src={featureData["image"]}
                alt="blog"
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "560px",
                  borderRadius: "12px",
                }}
              />
            </Box>

            {/* Table of Contents */}
            <Grid
              container
              spacing={2}
              direction="column"
              sx={{
                mb: 5,
                mt: 5,
                border: "solid 1px rgb(198, 197, 197)",
                fontSize: "0.9em",
                borderRadius: "10px",
                boxSizing: "border-box",
                p: 2,
                fontWeight: 500,
                color: "rgb(105, 104, 104)",
              }}
            >
              <Grid container direction="row" spacing={1} alignItems="center">
                <Grid
                  sx={{
                    backgroundColor: "rgb(32, 65, 182)",
                    width: "6px",
                    height: "20px",
                    borderRadius: "6px",
                    mr: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 500,
                    fontSize: "1.1em",
                    color: "black",
                  }}
                >
                  Table of Contents
                </Typography>
              </Grid>
              {featureData["content"].map((item, index) => (
                <Grid
                  item
                  key={index}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <ArrowForwardIosOutlinedIcon
                    sx={{ fontSize: "0.7em", mr: 1 }}
                  />{" "}
                  {item}
                </Grid>
              ))}
            </Grid>

            {/* Paragraphs */}
            <Box>
              {featureData["para"].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: item.spacingAfter || (item.type === "text" ? 2 : 1),
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  {item.type === "bullet" && (
                    <FiberManualRecordIcon
                      sx={{ fontSize: "0.7em", mt: "4px", mr: 1 }}
                    />
                  )}
                  <Typography
                    variant="inherit"
                    fontWeight={
                      item.type === "head"
                        ? "bold"
                        : item.type === "subhead"
                        ? 500
                        : 400
                    }
                    fontSize={
                      item.type === "head"
                        ? "1.3em"
                        : item.type === "subhead"
                        ? "1.1em"
                        : "1.1em"
                    }
                    dangerouslySetInnerHTML={{ __html: item.value }}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 7.4 }} />

            {/* Author Section */}
            {/* <Typography
            sx={{
              mb: 4,
              fontSize: { xs: "1.3em", md: "1.7em" },
              wordSpacing: "4px",
              fontWeight: 800,
              fontFamily: '"Roboto", sans-serif',
            }}
          >
            About the Author
          </Typography>

          <Card
            variant="outlined"
            sx={{ borderRadius: 2, backgroundColor: "#fdfdfd", mb: 4 }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "#0b3d91",
                  width: 48,
                  height: 48,
                  fontWeight: "bold",
                  mr: 2,
                }}
              >
                {featureData["initials"]}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {featureData["author"]}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  {featureData["occupation"]}
                </Typography>
                <Box>
                  <Link
                    href="#"
                    underline="hover"
                    sx={{
                      color: "#e66a00",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      mr: 2,
                    }}
                  >
                    LinkedIn
                  </Link>
                  <Link
                    href="#"
                    underline="hover"
                    sx={{
                      color: "#e66a00",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Website
                  </Link>
                </Box>
              </Box>
            </CardContent>
          </Card> */}

            <Divider sx={{ my: 2.4 }} />
          </Grid>

          {/* Right Section (Related Blogs) */}
          <Grid sx={{ flex: 0.35, my: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
              Related Blogs
            </Typography>
            <Card
              sx={{
                borderRadius: 2,
                backgroundColor: "#f7f7f7ff",
                boxShadow: 3,
                width: "100%",
                maxWidth: 290,
                mx: "auto",
                boxSizing: "border-box",
                overflow: "hidden",
                transition: "transform 0.5s, opacity 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  border: "2.4px solid #fff",
                },
              }}
            >
              <CardMedia
                component="img"
                height="185"
                image={nextBlogCard.img}
                alt={nextBlogCard.title}
              />
              <CardContent>
                <Button
                  href={nextBlogCard.href}
                  sx={{ textAlign: "left", p: 0 }}
                >
                  <Typography
                    variant="body1"
                    component="div"
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    {nextBlogCard.title}
                  </Typography>
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {nextBlogCard.desc}
                </Typography>
              </CardContent>
              <Button
                href={nextBlogCard.href}
                sx={{
                  px: 2,
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 550,
                  textTransform: "none",
                  fontSize: "1em",
                }}
              >
                Read more
              </Button>
            </Card>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
